import { ArrowUpRight, MapPin } from "lucide-react";
import type { Event, LocationConfig } from "@/lib/event-schema";

type Props = {
  config: LocationConfig;
  event: Event;
};

export function LocationModule({ config, event }: Props) {
  // Clamp zoom so the derived bbox stays inside sensible bounds. At
  // zoom=8 delta=0.16 (~17km), still a usable neighborhood view; below
  // that the bbox wraps absurdly and OSM degrades.
  const zoom = Math.min(19, Math.max(8, config.zoom ?? 14));
  const delta = 0.005 * 2 ** (14 - zoom);
  const minLng = Math.max(-180, config.lng - delta);
  const minLat = Math.max(-85, config.lat - delta);
  const maxLng = Math.min(180, config.lng + delta);
  const maxLat = Math.min(85, config.lat + delta);
  const osmEmbed = `https://www.openstreetmap.org/export/embed.html?bbox=${minLng}%2C${minLat}%2C${maxLng}%2C${maxLat}&layer=mapnik&marker=${config.lat}%2C${config.lng}`;
  const directions = `https://www.google.com/maps/dir/?api=1&destination=${config.lat},${config.lng}`;
  const showDirections = config.showDirections !== false;

  return (
    <section
      id="location"
      className="mx-auto w-full max-w-5xl px-5 py-16 sm:px-6 lg:px-8 lg:py-24"
      aria-labelledby={`${event.slug}-location`}
    >
      <header className="text-center mb-10">
        <p className="eyebrow text-accent inline-flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5" strokeWidth={1.75} />
          Find us
        </p>
        <h2
          id={`${event.slug}-location`}
          className="display-title mt-3 text-[clamp(2rem,4.4vw,3.25rem)] leading-tight"
        >
          {config.title ?? "Where we'll be"}
        </h2>
        {config.address ? (
          <p className="mx-auto mt-3 max-w-xl whitespace-pre-line text-balance text-muted">
            {config.address}
          </p>
        ) : null}
        {showDirections ? (
          <a
            href={directions}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get directions (opens in a new tab)"
            className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-medium text-canvas dark:bg-accent dark:text-accent-on shadow-glow hover:bg-ink/90 dark:hover:bg-accent/90 transition-colors focus-ring"
          >
            Get directions
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </a>
        ) : null}
      </header>

      <div className="surface-card overflow-hidden">
        <div className="relative aspect-[4/5] sm:aspect-[16/9] bg-paper">
          <iframe
            src={osmEmbed}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={`Map of ${config.address || "the venue"}`}
            className="absolute inset-0 h-full w-full"
            allow="geolocation"
          />
        </div>
      </div>
    </section>
  );
}
